'use client';

import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { UserPlus, TrendingUp, Users } from 'lucide-react';

interface Plan {
  name: string;
}

interface CommissionsSectionProps {
  plan: Plan;
}

export function CommissionsSection({ plan }: CommissionsSectionProps) {
  const t = useTranslations('planDetails.forcedMatrixPlan.commissions');

  return (
    <SectionWrapper variant="default" padding="md" className="relative bg-gray-50 dark:bg-gray-900/50">
      <div className="container">
        <div className="mb-12 text-center">
          <Typography variant="h2" className="mb-4">
            {t('title')}
          </Typography>
          {t('description') && (
            <Typography variant="p" className="text-gray-600 dark:text-gray-400 max-w-4xl mx-auto">
              {t('description')}
            </Typography>
          )}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {/* Sponsor/Referral Commission */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border-2 border-primary-200 dark:border-primary-800 hover:border-primary-400 dark:hover:border-primary-600 transition-all duration-300 hover:shadow-xl">
            <div className="flex flex-col items-center text-center mb-4">
              <div className="w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mb-4">
                <UserPlus className="w-8 h-8 text-primary-600 dark:text-primary-400" />
              </div>
              <Typography variant="h3" className="text-lg font-bold text-gray-900 dark:text-white">
                {t('sponsor.title')}
              </Typography>
            </div>
            <Typography variant="p" className="text-gray-700 dark:text-gray-300 leading-relaxed text-center">
              {t('sponsor.description')}
            </Typography>
          </div>

          {/* Level Commission */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border-2 border-blue-200 dark:border-blue-800 hover:border-blue-400 dark:hover:border-blue-600 transition-all duration-300 hover:shadow-xl">
            <div className="flex flex-col items-center text-center mb-4">
              <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-4">
                <TrendingUp className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <Typography variant="h3" className="text-lg font-bold text-gray-900 dark:text-white">
                {t('level.title')}
              </Typography>
            </div>
            <Typography variant="p" className="text-gray-700 dark:text-gray-300 leading-relaxed text-center">
              {t('level.description')}
            </Typography>
          </div>

          {/* Stage/Matrix Commission */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border-2 border-green-200 dark:border-green-800 hover:border-green-400 dark:hover:border-green-600 transition-all duration-300 hover:shadow-xl">
            <div className="flex flex-col items-center text-center mb-4">
              <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
                <Users className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <Typography variant="h3" className="text-lg font-bold text-gray-900 dark:text-white">
                {t('stage.title')}
              </Typography>
            </div>
            <Typography variant="p" className="text-gray-700 dark:text-gray-300 leading-relaxed text-center">
              {t('stage.description')}
            </Typography>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
}

